import { useEffect, useMemo, useState } from 'react';
import { catalogApi, utilApi, type CourtCalendarEntry } from '../../api';
import Select from '../../components/common/Select';

// Read-only view of ZA public holidays + High Court recess so tenants can see
// why a deadline fell where it did. Includes a quick deadline-calculator that
// hits /util/deadline so the answer always matches the server arithmetic.

export default function SettingsCourtCalendar() {
  const thisYear = new Date().getUTCFullYear();
  const [year, setYear] = useState(thisYear);
  const [entries, setEntries] = useState<CourtCalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Deadline calculator state
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [offset, setOffset] = useState(10);
  const [mode, setMode] = useState<'court' | 'calendar'>('court');
  const [result, setResult] = useState<{ due_date: string; weekday: string; is_court_day: boolean } | null>(null);
  const [calcBusy, setCalcBusy] = useState(false);
  const [calcError, setCalcError] = useState<string | null>(null);

  const load = async (y: number) => {
    setLoading(true); setError(null);
    try {
      const r = await catalogApi.courtCalendar('ZA', `${y}-01-01`, `${y}-12-31`);
      setEntries(r.entries);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(year); }, [year]);

  const calc = async () => {
    setCalcBusy(true); setCalcError(null); setResult(null);
    try {
      const r = await utilApi.computeDeadline({ from, offset_days: Number(offset), mode });
      setResult({ due_date: r.due_date, weekday: r.weekday, is_court_day: r.is_court_day });
    } catch (e) { setCalcError((e as Error).message); }
    finally { setCalcBusy(false); }
  };

  const byMonth = useMemo(() => {
    const out: Record<string, CourtCalendarEntry[]> = {};
    for (const e of entries) {
      const key = e.court_date.slice(0, 7);
      (out[key] = out[key] || []).push(e);
    }
    return out;
  }, [entries]);

  const yearOptions = [];
  for (let y = thisYear - 1; y <= thisYear + 2; y++) yearOptions.push({ value: String(y), label: String(y) });

  return (
    <div className="stack" style={{ maxWidth: 860 }}>
      <div>
        <h1>Court calendar</h1>
        <p className="muted">
          South African High Court recess periods and statutory public holidays. Deadlines that tick on "court days" skip every date listed here — so you can see why a 10-court-day deadline landed where it did.
        </p>
      </div>

      <div className="card stack">
        <h3 style={{ margin: 0 }}>Deadline calculator</h3>
        <div className="row">
          <div className="grow">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div style={{ width: 140 }}>
            <label>Offset (days)</label>
            <input type="number" value={offset} onChange={(e) => setOffset(Number(e.target.value))} />
          </div>
          <div style={{ width: 180 }}>
            <label>Mode</label>
            <Select
              value={mode}
              onChange={(v) => setMode(v as 'court'|'calendar')}
              options={[
                { value: 'court',    label: 'Court days (skip non-court)' },
                { value: 'calendar', label: 'Calendar days' },
              ]}
            />
          </div>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="primary" onClick={calc} disabled={calcBusy}>
            {calcBusy ? 'Calculating…' : 'Compute'}
          </button>
        </div>
        {calcError && <div style={{ color: 'var(--color-danger)' }}>{calcError}</div>}
        {result && (
          <div style={{ padding: 12, background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: 8 }}>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{result.due_date}</div>
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              {result.weekday} — {result.is_court_day ? 'court day' : 'not a court day'}
            </div>
          </div>
        )}
      </div>

      <div className="row">
        <h3 style={{ margin: 0 }} className="grow">Year</h3>
        <div style={{ width: 140 }}>
          <Select
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            options={yearOptions}
          />
        </div>
      </div>

      {error && <div className="card" style={{ borderColor: 'var(--color-danger)' }}>
        <b>Could not load calendar</b>
        <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>{error}</p>
      </div>}

      {loading ? (
        <div className="muted">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="card"><p className="muted">No calendar entries seeded for {year}. Run the seeding script.</p></div>
      ) : (
        Object.entries(byMonth).map(([month, days]) => (
          <div key={month}>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 6 }}>
              {new Date(month + '-01T00:00:00Z').toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
            </div>
            <div className="card" style={{ padding: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 140 }}>Date</th>
                    <th style={{ width: 100 }}>Kind</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.ROWID}>
                      <td>{new Date(d.court_date + 'T00:00:00Z').toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                      <td>
                        <span className={`pill ${d.kind === 'holiday' ? 'accent' : ''}`}>
                          {d.kind}
                        </span>
                      </td>
                      <td>{d.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
